/**
 * Buddy Roster — names + locks + per-tier unlock rules.
 *
 * The default buddy is **Saathi** (the lotus mascot). Free Basic users see all
 * four roster cards but only Saathi is unlocked; the rest carry a lock and
 * route to /subscription on click.
 *
 * Admin can override `unlock_tier` per buddy from /api/admin/settings →
 * `buddy_roster`, but defaults are baked here so the UI works immediately.
 */
import React from 'react';
import { motion } from 'framer-motion';

// Phase 2.19 roster ordering:
//   1. Chirpy (free Rive 2D — universal default for every plan, including Basic)
//   2. Bhalu  (Original buddy unlocked at Basic Lifetime)
//   3-7. Saathi / Unico / Red Panda / Wolfy (Growth-tier originals)
//   8-9. Cutie Pie / Drago (Premium / Elite originals)
// Mira / Jiggy / Centaurion were removed — they were placeholder reuses of
// birb.riv that didn't visually differentiate enough to be real buddies.
export const BUDDY_IDS = ['chirpy', 'bhalu', 'saathi', 'unico', 'redpanda', 'wolfy', 'cutiepie', 'drago'];

export const BUDDY_ROSTER = {
  // ============ FREE / DEFAULT — Rive 2D rigged ============
  chirpy: {
    id: 'chirpy',
    name: 'Chirpy',
    tagline: 'Your free study buddy',
    description: 'A bouncy, eternally optimistic feathered friend. The buddy every learner starts with.',
    unlock_tier: 'basic',
    image_url: '/buddies/chirpy.png',
    accent: '#0EA5E9',
    palette: { body: '#BAE6FD', shadow: '#0284C7', crown: '#FCD34D', petals: '#0EA5E9' },
    badge: 'Free for everyone',
  },
  // ============ BASIC LIFETIME loyalty perk ============
  bhalu: {
    id: 'bhalu',
    name: 'Bhalu',
    tagline: 'The studious honey-bear',
    description: 'A scholarly bear with little glasses. Loves homework AND honey \u2014 best of both worlds.',
    unlock_tier: 'basic_lifetime',
    image_url: '/buddies/bhalu.jpg',
    accent: '#92400E',
    palette: { body: '#FED7AA', shadow: '#92400E', crown: '#F59E0B', petals: '#FBBF24' },
  },
  // ============ GROWTH-tier originals ============
  saathi: {
    id: 'saathi',
    name: 'Saathi',
    tagline: 'Your calm companion',
    description: 'A warm friend who never judges. Patient with every doubt, big or small.',
    unlock_tier: 'growth',
    image_url: '/buddies/saathi.png',
    accent: '#F5A972',
    palette: { body: '#FFD9A8', shadow: '#F5A972', crown: '#F472B6', petals: '#FB923C' },
  },
  unico: {
    id: 'unico',
    name: 'Unico',
    tagline: 'The sparkly motivator',
    description: 'Magical baby unicorn. Brings rainbow energy and dopamine to every quiz win.',
    unlock_tier: 'growth',
    image_url: '/buddies/unico.jpg',
    accent: '#A78BFA',
    palette: { body: '#FFFFFF', shadow: '#E9D5FF', crown: '#F0ABFC', petals: '#FB7185' },
  },
  redpanda: {
    id: 'redpanda',
    name: 'Red Panda',
    tagline: 'The cheerful explorer',
    description: 'A bamboo-loving fluffy friend. Always ready to dive into a new topic.',
    unlock_tier: 'growth',
    image_url: '/buddies/redpanda.jpg',
    accent: '#DC2626',
    palette: { body: '#FECACA', shadow: '#DC2626', crown: '#F97316', petals: '#FB923C' },
  },
  wolfy: {
    id: 'wolfy',
    name: 'Wolfy',
    tagline: 'The thoughtful snow-fox',
    description: 'A gentle, sky-eyed snow-fox spirit. Listens deeply, speaks softly, never rushes you.',
    unlock_tier: 'premium',
    image_url: '/buddies/wolfy.jpg',
    accent: '#38BDF8',
    palette: { body: '#E0F2FE', shadow: '#0284C7', crown: '#A5F3FC', petals: '#67E8F9' },
  },
  // ============ PREMIUM / ELITE originals ============
  cutiepie: {
    id: 'cutiepie',
    name: 'Cutie Pie',
    tagline: 'The dreamy axolotl',
    description: 'A pastel pink water-friend with the biggest, kindest eyes. Pure dreamy energy.',
    unlock_tier: 'premium',
    image_url: '/buddies/cutiepie.jpg',
    accent: '#EC4899',
    palette: { body: '#FBCFE8', shadow: '#EC4899', crown: '#F472B6', petals: '#FDA4AF' },
  },
  drago: {
    id: 'drago',
    name: 'Drago',
    tagline: 'The crystal dragon',
    description: 'A mythical baby dragon from a crystal cave. Quietly powerful, fiercely loyal.',
    unlock_tier: 'premium',
    image_url: '/buddies/drago.jpg',
    accent: '#10B981',
    palette: { body: '#A7F3D0', shadow: '#10B981', crown: '#A78BFA', petals: '#7C3AED' },
  },
};

// Phase 2.19: basic_lifetime gets its own rank between basic and growth so
// Bhalu can be unlocked exclusively for that tier. Must mirror the backend
// BUDDY_TIER_RANK constant exactly.
const TIER_RANK = { basic: 0, basic_lifetime: 1, growth: 2, premium: 3, elite: 3 };

export function isBuddyUnlocked(buddyId, userTier, userRole) {
  const b = BUDDY_ROSTER[buddyId];
  if (!b) return false;
  if (userRole === 'admin' || userRole === 'support_staff') return true;
  return (TIER_RANK[(userTier || 'basic').toLowerCase()] ?? 0) >= (TIER_RANK[b.unlock_tier] ?? 0);
}

/**
 * BuddyAvatar — renders a buddy. Uses the user-supplied PNG/JPG with
 * character-agnostic animations (blink, breath, squash, sparkle particles)
 * so even raster images feel alive across the full mood library.
 *
 * For buddies with `kind: 'rive'` we render the .riv character directly via
 * lazy-loaded `RiveCharacter`. Rive characters do their own breathing/blinking
 * so the framer-motion mood overlays stay light (just the aura + speaking dots).
 */
export function BuddyAvatar({ buddyId, mood = 'idle', isSpeaking = false, size = 80 }) {
  const buddy = BUDDY_ROSTER[buddyId] || BUDDY_ROSTER.saathi;
  const [imgError, setImgError] = React.useState(false);
  const [blink, setBlink] = React.useState(false);
  const [RiveComp, setRiveComp] = React.useState(null);

  // Lazy-load the Rive character renderer only when actually needed — keeps
  // the bundle slim for users who picked a JPG buddy.
  React.useEffect(() => {
    if (buddy.kind !== 'rive') return;
    let cancelled = false;
    import('@/components/RiveBuddy').then(mod => {
      if (!cancelled) setRiveComp(() => mod.RiveCharacter);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [buddy.kind]);

  // Random blinks every 3-7 seconds — the single biggest "alive" cue.
  React.useEffect(() => {
    let timer;
    const schedule = () => {
      const delay = 3000 + Math.random() * 4000;
      timer = setTimeout(() => {
        setBlink(true);
        setTimeout(() => setBlink(false), 130);
        schedule();
      }, delay);
    };
    schedule();
    return () => clearTimeout(timer);
  }, []);

  // Per-mood transform / animation. Body squash & tilt convey emotion even
  // when the underlying image is static.
  const moodAnim = {
    idle:       { scale: [1, 1.02, 1], rotate: [0, 0, 0] },
    thinking:   { scale: [1, 0.98, 1], rotate: [-2, 1, -2], y: [0, -1, 0] },
    explaining: { scale: [1, 1.03, 0.99, 1], y: [0, -3, 0, 0] },
    happy:      { scale: [1, 1.06, 1], rotate: [-3, 3, -3], y: [0, -3, 0] },
    cheering:   { scale: [1, 1.18, 0.95, 1.1, 1], rotate: [-6, 6, -4, 4, 0], y: [0, -10, -2, -6, 0] },
    consoling:  { scale: [1, 0.97, 1], rotate: [0, -2, 0], y: [0, 1, 0] },
    curious:    { scale: [1, 1.02, 1], rotate: [-3, 3, -3] },
  }[mood] || { scale: 1 };

  const transitionByMood = {
    cheering:   { duration: 0.8, repeat: Infinity, ease: 'easeInOut' },
    explaining: { duration: 0.55, repeat: Infinity, ease: 'easeInOut' },
    happy:      { duration: 1.4, repeat: Infinity, ease: 'easeInOut' },
    consoling:  { duration: 2.2, repeat: Infinity, ease: 'easeInOut' },
    thinking:   { duration: 1.8, repeat: Infinity, ease: 'easeInOut' },
    curious:    { duration: 1.0, repeat: Infinity, ease: 'easeInOut' },
    idle:       { duration: 2.4, repeat: Infinity, ease: 'easeInOut' },
  }[mood] || { duration: 2.4, repeat: Infinity };

  // Mood aura — colored glow ring that morphs with emotion
  const auraColor = {
    idle:       'shadow-amber-200/0',
    thinking:   'shadow-blue-300/60',
    explaining: 'shadow-emerald-300/60',
    happy:      'shadow-amber-300/70',
    cheering:   'shadow-fuchsia-400/80',
    consoling:  'shadow-rose-300/60',
    curious:    'shadow-purple-300/60',
  }[mood] || 'shadow-amber-200/0';

  // ============ RIVE 2D RIGGED RENDER PATH ============
  if (buddy.kind === 'rive') {
    const auraScale = mood === 'cheering' ? [1, 1.3, 1] : [1, 1.05, 1];
    return (
      <div data-testid={`buddy-avatar-${buddy.id}`} className="relative inline-block" style={{ width: size, height: size }}>
        <motion.div
          className={`absolute inset-0 rounded-full ${auraColor} blur-md`}
          animate={{ scale: auraScale, opacity: mood === 'cheering' ? [0.3, 0.7, 0.3] : [0.4, 0.6, 0.4] }}
          transition={{ duration: 1.4, repeat: Infinity }}
          aria-hidden="true"
        />
        {RiveComp ? (
          <RiveComp src={buddy.rive_src} stateMachine={buddy.rive_state_machine} size={size}
            testId={`rive-${buddy.id}`}
            className="rounded-2xl overflow-hidden bg-white/40" />
        ) : (
          // Loading skeleton — shows briefly while @rive-app/react-canvas
          // dynamic-imports. Keeps layout stable.
          <div className="w-full h-full rounded-2xl bg-gradient-to-br from-slate-50 to-slate-100 animate-pulse" />
        )}
        {isSpeaking && (
          <div className="absolute bottom-1 left-1/2 -translate-x-1/2 flex gap-0.5 z-10" aria-hidden="true">
            {[0, 1, 2].map(i => (
              <motion.span key={i}
                className="w-1 h-1 rounded-full bg-white shadow"
                animate={{ scaleY: [1, 1.8, 1] }}
                transition={{ duration: 0.4, repeat: Infinity, delay: i * 0.12 }}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  // Floating mood particles — emoji-free SVG dots/stars/Z's that orbit the buddy
  const showSparkle = mood === 'cheering' || mood === 'happy';
  const showThinkBubble = mood === 'thinking';
  const showHearts = mood === 'consoling';
  const showQuestion = mood === 'curious';

  if (buddy.image_url && !imgError) {
    return (
      <div data-testid={`buddy-avatar-${buddy.id}`} className="relative inline-block" style={{ width: size, height: size }}>
        {/* Aura — soft colored shadow that pulses by mood */}
        <motion.div
          className={`absolute inset-0 rounded-full ${auraColor} blur-md`}
          style={{ boxShadow: 'inset 0 0 0 0' }}
          animate={mood === 'cheering' ? { scale: [1, 1.3, 1], opacity: [0.3, 0.6, 0.3] } : { opacity: 0.4 }}
          transition={{ duration: 1, repeat: Infinity }}
          aria-hidden="true"
        />

        <motion.img
          src={buddy.image_url}
          alt={buddy.name}
          width={size} height={size}
          loading="lazy"
          onError={() => setImgError(true)}
          className="relative w-full h-full object-cover rounded-2xl select-none"
          draggable={false}
          style={{ transform: blink ? 'scaleY(0.1)' : undefined, transformOrigin: '50% 45%', transition: 'transform 0.13s ease-in-out' }}
          animate={moodAnim}
          transition={transitionByMood}
        />

        {/* Speaking indicator: small bouncing equalizer-style dots near the mouth */}
        {isSpeaking && (
          <div className="absolute bottom-1 left-1/2 -translate-x-1/2 flex gap-0.5 z-10" aria-hidden="true">
            {[0, 1, 2].map(i => (
              <motion.span key={i}
                className="w-1 h-1 rounded-full bg-white shadow"
                animate={{ scaleY: [1, 1.8, 1] }}
                transition={{ duration: 0.4, repeat: Infinity, delay: i * 0.12 }}
              />
            ))}
          </div>
        )}

        {/* Cheering / happy sparkles */}
        {showSparkle && (
          <>
            <motion.span className="absolute -top-1 -right-1 text-amber-400 text-lg pointer-events-none"
              animate={{ scale: [0, 1.2, 0], rotate: [0, 180, 360], opacity: [0, 1, 0] }}
              transition={{ duration: 1.2, repeat: Infinity }}
              aria-hidden="true"
            >&#10024;</motion.span>
            <motion.span className="absolute -bottom-1 -left-1 text-fuchsia-400 text-base pointer-events-none"
              animate={{ scale: [0, 1, 0], rotate: [180, 0, -180], opacity: [0, 1, 0] }}
              transition={{ duration: 1.4, repeat: Infinity, delay: 0.3 }}
              aria-hidden="true"
            >&#10024;</motion.span>
            <motion.span className="absolute top-2 -left-2 text-yellow-400 text-xs pointer-events-none"
              animate={{ y: [-5, -20], opacity: [0, 1, 0] }}
              transition={{ duration: 1.6, repeat: Infinity, delay: 0.6 }}
              aria-hidden="true"
            >&#9733;</motion.span>
          </>
        )}

        {/* Thinking bubble */}
        {showThinkBubble && (
          <motion.div className="absolute -top-2 -right-2 flex flex-col items-end gap-0.5 pointer-events-none" aria-hidden="true">
            <motion.span className="w-1.5 h-1.5 rounded-full bg-white ring-1 ring-slate-300"
              animate={{ scale: [0.6, 1, 0.6], opacity: [0.4, 1, 0.4] }} transition={{ duration: 1.4, repeat: Infinity, delay: 0 }} />
            <motion.span className="w-2 h-2 rounded-full bg-white ring-1 ring-slate-300"
              animate={{ scale: [0.6, 1, 0.6], opacity: [0.4, 1, 0.4] }} transition={{ duration: 1.4, repeat: Infinity, delay: 0.3 }} />
            <motion.span className="w-3 h-3 rounded-full bg-white ring-1 ring-slate-300 flex items-center justify-center text-[8px] font-heading text-slate-500"
              animate={{ scale: [0.8, 1, 0.8], opacity: [0.6, 1, 0.6] }} transition={{ duration: 1.4, repeat: Infinity, delay: 0.6 }}>?</motion.span>
          </motion.div>
        )}

        {/* Consoling hearts */}
        {showHearts && (
          <>
            <motion.span className="absolute top-1 -right-1 text-rose-400 text-sm pointer-events-none"
              animate={{ y: [0, -16], opacity: [0, 1, 0], scale: [0.5, 1, 0.7] }}
              transition={{ duration: 2, repeat: Infinity }}
              aria-hidden="true"
            >&#9829;</motion.span>
            <motion.span className="absolute top-3 -left-1 text-pink-300 text-xs pointer-events-none"
              animate={{ y: [0, -14], opacity: [0, 1, 0], scale: [0.5, 1, 0.7] }}
              transition={{ duration: 2.2, repeat: Infinity, delay: 0.7 }}
              aria-hidden="true"
            >&#9829;</motion.span>
          </>
        )}

        {/* Curious — single bouncing question mark */}
        {showQuestion && (
          <motion.span className="absolute -top-2 right-2 text-purple-500 font-heading text-base pointer-events-none"
            animate={{ y: [0, -3, 0], rotate: [-8, 8, -8] }}
            transition={{ duration: 0.9, repeat: Infinity }}
            aria-hidden="true"
          >?</motion.span>
        )}
      </div>
    );
  }

  // SVG fallback (kept simple — only triggers if PNG fails to load)
  return (
    <svg viewBox="0 0 100 100" style={{ width: size, height: size }} data-testid={`buddy-avatar-${buddy.id}`}>
      <defs>
        <radialGradient id={`b-grad-${buddy.id}`} cx="0.4" cy="0.35">
          <stop offset="0%" stopColor="#FFFFFF" />
          <stop offset="60%" stopColor={buddy.palette.body} />
          <stop offset="100%" stopColor={buddy.palette.shadow} />
        </radialGradient>
      </defs>
      <circle cx="50" cy="52" r="38" fill={`url(#b-grad-${buddy.id})`} />
      <ellipse cx="38" cy="48" rx="5" ry="6" fill="#fff" />
      <ellipse cx="62" cy="48" rx="5" ry="6" fill="#fff" />
      <circle cx="38" cy="48" r="2.5" fill="#1F2937" />
      <circle cx="62" cy="48" r="2.5" fill="#1F2937" />
      <path d="M40,62 Q50,68 60,62" stroke="#7C2D12" strokeWidth="2.5" strokeLinecap="round" fill="none" />
    </svg>
  );
}
